import { lazy, Suspense, useMemo, useState } from 'react'
import { Button } from '@bloo-team/ui'
import type { CmScope, CmSignalLink, CmStatus } from '@bloo-team/api-client'
import { caseNetworkGraph, fadedKind, NETWORK_CASES_CAP, pickCases, pickSignalIds, type NetworkCase } from '../../lib/case-network-graph'
import { caseLabel, statusLabel, useCmCaseSignals } from '../../lib/case-mgmt'
import { readSignal, type Signal } from '../../lib/signal-model'
import { SIGNAL_ROWS_CAP, useSignalRows } from '../../lib/signals'
import { GraphErrorBoundary } from '../signal/force/GraphErrorBoundary'
import type { NetworkSelection } from './CaseNetworkCanvas'

/** The canvas chunk (force-graph) loads only when the graph shows — as the Details graph. */
const loadCanvas = () => import('./CaseNetworkCanvas')
let Canvas = lazy(loadCanvas)
const retryCanvas = () => {
  Canvas = lazy(loadCanvas)
}

/**
 * Cases' signals as one graph, each case a dotted box (docs/cases.md §Case
 * page · Relations, Clusters). Reads each case's attached signals (the first
 * NETWORK_CASES_CAP cases, nearest first; one workspace read each — CB-86),
 * then the newest SIGNAL_ROWS_CAP signals across them with their suspects
 * and targets (one read each — CB-85); builds the graph (case-network-graph)
 * and hands it to the canvas. Beside it, the side card of the selection: a
 * case (Open; Link for a candidate), a signal (Open in Signals) or an entity.
 */
export function CaseNetworkGraph({
  scope,
  cases,
  statuses,
  onOpenCase,
  onOpenSignal,
  link,
  label,
}: {
  scope: CmScope
  /** This case first, then the rest nearest first. */
  cases: readonly NetworkCase[]
  statuses: readonly CmStatus[]
  onOpenCase: (c: NetworkCase) => void
  onOpenSignal: (uuid: string) => void
  /** Relations: Link a candidate from its card. */
  link?: { run: (caseId: string) => void; busy: boolean; error: { caseId: string; message: string } | null }
  label: string
}) {
  const [selected, setSelected] = useState<NetworkSelection>(null)
  const [fit, setFit] = useState(0)
  const [attempt, setAttempt] = useState(0)

  const picked = useMemo(() => pickCases(cases), [cases])
  const caseQs = useCmCaseSignals(scope, picked.map((c) => c.id))
  const casesKey = caseQs.map((q) => `${q.status}:${q.dataUpdatedAt}`).join('|')
  const pendingCases = caseQs.filter((q) => q.isLoading).length
  // The signal window is picked once the first round of case reads has settled: picked while they land,
  // every case that lands would push new ids into it, each one more read (no batch read, CB-85).
  const [casesReady, setCasesReady] = useState(false)
  if (!casesReady && !pendingCases) setCasesReady(true)
  const links = useMemo(() => {
    const m = new Map<string, readonly CmSignalLink[]>()
    picked.forEach((c, i) => m.set(c.id, caseQs[i]?.data ?? []))
    return m
    // eslint-disable-next-line react-hooks/exhaustive-deps -- casesKey stands for caseQs (a new array every render)
  }, [picked, casesKey])
  const unreadCases = caseQs.filter((q) => q.isError).length

  const allIds = useMemo(() => (casesReady ? pickSignalIds(picked, links) : []), [casesReady, picked, links])
  const { ids, results, total } = useSignalRows(scope, allIds, casesReady)
  const readKey = results.map((r) => `${r.status}:${r.dataUpdatedAt}`).join('|')
  const pendingSignals = results.filter((r) => r.isLoading).length
  const unreadSignals = results.filter((r) => r.isError || (r.isSuccess && !r.data)).length

  const net = useMemo(() => {
    const rows = new Map<string, Signal | null>()
    ids.forEach((id, i) => {
      const row = results[i]?.data
      rows.set(id, row ? readSignal(row) : null)
    })
    return caseNetworkGraph(picked, links, ids, rows)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- readKey stands for results (a new array every render)
  }, [picked, links, ids, readKey])

  const boxes = useMemo(
    () => picked.map((c) => ({ id: c.id, label: caseLabel(c), self: c.kind === 'this', faded: fadedKind(c.kind) })),
    [picked],
  )

  // The canvas lays out again on every new graph (settled before it is shown), so it gets one only when no
  // read is in flight: the first load shows whole, and a case read later (one just linked) fills in once.
  const settled = useMemo(() => ({ picked, net, boxes }), [picked, net, boxes])
  const [shown, setShown] = useState<typeof settled | null>(null)
  if (casesReady && !pendingCases && !pendingSignals && shown !== settled) setShown(settled)

  if (!shown) {
    return (
      <p className="hud-unit" role="status">
        {casesReady ? `Reading ${ids.length} signals…` : `Reading the signals of ${picked.length} cases…`}
      </p>
    )
  }
  if (!shown.net.graph.nodes.length) {
    return unreadCases ? (
      <p className="sy-error" role="alert">
        {unreadCases} of {picked.length} cases could not be read: {caseQs.find((q) => q.isError)?.error?.message}
      </p>
    ) : (
      <p className="hud-unit">No signals in these cases.</p>
    )
  }

  const notes = [
    pendingCases || pendingSignals ? `Reading ${pendingCases ? `${pendingCases} more cases` : `${pendingSignals} more signals`}…` : '',
    cases.length > NETWORK_CASES_CAP ? `Showing ${NETWORK_CASES_CAP} of ${cases.length} cases.` : '',
    total > SIGNAL_ROWS_CAP ? `Showing the first ${SIGNAL_ROWS_CAP} of ${total} signals.` : '',
    unreadCases ? `${unreadCases} of ${picked.length} cases could not be read — shown without their signals.` : '',
    unreadSignals ? `${unreadSignals} of ${ids.length} signals could not be read — shown without their entities.` : '',
  ].filter(Boolean)

  return (
    <div className="cs-net">
      <div className="cs-net-main">
        <div className="hud-row hud-row-between cs-net-head">
          <span className="hud-unit">{notes.join(' ') || 'Click a case’s label, a signal or an entity to see it here.'}</span>
          <Button variant="outline" size="sm" tone="neutral" onPress={() => setFit((n) => n + 1)}>
            Fit
          </Button>
        </div>
        <GraphErrorBoundary
          key={attempt}
          onRetry={() => {
            retryCanvas()
            setAttempt((a) => a + 1)
          }}
        >
          <Suspense
            fallback={
              <p className="hud-unit" role="status">
                Loading graph…
              </p>
            }
          >
            <Canvas graph={shown.net.graph} members={shown.net.members} boxes={shown.boxes} selected={selected} onSelect={setSelected} fitSignal={fit} label={label} />
          </Suspense>
        </GraphErrorBoundary>
      </div>
      <SideCard
        selected={selected}
        cases={shown.picked}
        net={shown.net}
        statuses={statuses}
        onOpenCase={onOpenCase}
        onOpenSignal={onOpenSignal}
        link={link}
      />
    </div>
  )
}

const kindText = (c: NetworkCase) =>
  c.kind === 'this'
    ? 'this case'
    : c.kind === 'cluster'
      ? `hop ${c.hop ?? '—'}`
      : c.kind === 'candidate'
        ? 'candidate · not linked'
        : `${c.kind === 'dismissed' ? 'dismissed' : 'related'}${c.linkedBy ? ` · ${c.linkedBy}` : ''}`

function SideCard({
  selected,
  cases,
  net,
  statuses,
  onOpenCase,
  onOpenSignal,
  link,
}: {
  selected: NetworkSelection
  cases: readonly NetworkCase[]
  net: ReturnType<typeof caseNetworkGraph>
  statuses: readonly CmStatus[]
  onOpenCase: (c: NetworkCase) => void
  onOpenSignal: (uuid: string) => void
  link?: { run: (caseId: string) => void; busy: boolean; error: { caseId: string; message: string } | null }
}) {
  const c = selected?.kind === 'case' ? cases.find((x) => x.id === selected.id) : undefined
  const n = selected?.kind === 'node' ? net.graph.nodes.find((x) => x.id === selected.id) : undefined
  return (
    <aside className="hud-panel cs-net-card" aria-label="Selected" aria-live="polite">
      {c ? (
        <>
          <span className="hud-eyebrow">Case · {kindText(c)}</span>
          <b className="cs-net-card-title">{c.title}</b>
          <dl className="cs-net-kv">
            <dt>Case</dt>
            <dd className="mono">{caseLabel(c)}</dd>
            <dt>Severity</dt>
            <dd>{c.severity}</dd>
            <dt>Status</dt>
            <dd>{statusLabel(statuses, c.status)}</dd>
            <dt>Signals</dt>
            <dd className="mono">{net.signalCount.get(c.id) ?? 0}</dd>
          </dl>
          <span className="hud-row cs-net-actions">
            {c.kind !== 'this' ? (
              <Button variant="solid" size="sm" onPress={() => onOpenCase(c)}>
                Open {caseLabel(c)}
              </Button>
            ) : null}
            {c.kind === 'candidate' && link ? (
              <Button variant="outline" size="sm" isDisabled={link.busy} onPress={() => link.run(c.id)}>
                Link
              </Button>
            ) : null}
          </span>
          {link?.error?.caseId === c.id ? (
            <p className="sy-error-text" role="alert">
              {link.error.message}
            </p>
          ) : null}
        </>
      ) : n?.glyph?.role === 'signal' ? (
        <>
          <span className="hud-eyebrow">Signal · {n.glyph.severity ?? 'info'}</span>
          <b className="cs-net-card-title">{n.value}</b>
          <span className="hud-row cs-net-actions">
            <Button variant="solid" size="sm" onPress={() => onOpenSignal(n.id.replace(/^sig:/, ''))}>
              Open in Signals
            </Button>
          </span>
        </>
      ) : n ? (
        <>
          <span className="hud-eyebrow">Entity</span>
          <dl className="cs-net-kv">
            <dt>Type</dt>
            <dd>{n.field.toLowerCase()}</dd>
            <dt>Name</dt>
            <dd className="mono">{n.value}</dd>
            <dt>Role</dt>
            <dd>{n.glyph?.role ?? '—'}</dd>
          </dl>
        </>
      ) : (
        <>
          <span className="hud-eyebrow">Nothing selected</span>
          <p className="hud-unit">Click a case’s label, a signal or an entity to see it here.</p>
        </>
      )}
    </aside>
  )
}
